import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from './SecondarySidebar.module.css';
import { Tr, tr } from '@/i18n';
import { useAdminAccess } from '@/hooks/useAdminAccess';

interface NavItem {
  path: string;
  label: string;
  langKey?: string;
  hasSubmenu?: boolean;
  children?: NavItem[];
}

interface SecondarySidebarProps {
  title: string;
  titleKey?: string;
  navItems: NavItem[];
}

export const SecondarySidebar: React.FC<SecondarySidebarProps> = ({ title, titleKey, navItems: allNavItems }) => {
  const location = useLocation();
  // UAT DEF-174: leave out the pages this user can't open (platform-level pages for tenant admins; everything but
  // their course pages for a course creator who isn't an administrator).
  const { canOpen } = useAdminAccess();
  const navItems = useMemo(
    () => allNavItems
      .map((i) => (i.children ? { ...i, children: i.children.filter((c) => canOpen(c.path)) } : i))
      .filter((i) => canOpen(i.path) && (!i.children || i.children.length > 0)),
    [allNavItems, canOpen],
  );
  const storageKey = `sidebar_collapsed_${title}`;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  // Which parent sections are expanded (path → open).
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    try { localStorage.setItem(storageKey, String(isCollapsed)); } catch {}
  }, [isCollapsed, storageKey]);

  // Auto-expand the section containing the current route.
  useEffect(() => {
    const owner = navItems.find(
      (i) => i.children?.length && (location.pathname === i.path || location.pathname.startsWith(i.path + '/')),
    );
    if (owner) setOpenSections((prev) => (prev[owner.path] ? prev : { ...prev, [owner.path]: true }));
  }, [location.pathname, navItems]);

  const toggle = () => setIsCollapsed(c => !c);

  // Collect all sibling paths so we can disambiguate overlapping prefixes.
  // e.g. "/admin/learning/courses" vs "/admin/learning/courses/categories"
  const allPaths = navItems.map(i => i.path);

  const isItemActive = (itemPath: string, currentPathname: string): boolean => {
    // Exact match is always active
    if (currentPathname === itemPath || currentPathname === itemPath + '/') return true;
    // Prefix match — but only if no OTHER sibling is a longer/better match
    if (currentPathname.startsWith(itemPath + '/') || currentPathname.startsWith(itemPath + '?')) {
      const hasLongerSibling = allPaths.some(
        p => p !== itemPath && p.startsWith(itemPath) && currentPathname.startsWith(p)
      );
      return !hasLongerSibling;
    }
    return false;
  };

  const isChildActive = (childPath: string, currentPathname: string): boolean =>
    currentPathname === childPath ||
    currentPathname === childPath + '/' ||
    currentPathname.startsWith(childPath + '/');

  return (
    <aside className={`${styles.secondarySidebar} ${isCollapsed ? styles.collapsed : ''}`}>
      {/* Collapse toggle button */}
      <button
        className={styles.collapseBtn}
        onClick={toggle}
        title={isCollapsed ? tr("ui.expand_menu", "Expand menu") : tr("ui.collapse_menu", "Collapse menu")}
        aria-label={isCollapsed ? tr("ui.expand_menu", "Expand menu") : tr("ui.collapse_menu", "Collapse menu")}
      >
        <span className={styles.collapseArrow}>{isCollapsed ? '»' : '«'}</span>
      </button>

      <h2 className={`${styles.title} h2`}>
        {titleKey ? <Tr k={titleKey} /> : title}
      </h2>
      <nav className={styles.nav}>
        <ul className={styles.navList}>
          {navItems.map((item) => {
            const active = isItemActive(item.path, location.pathname);
            const hasChildren = !!item.children?.length;

            // Parents with children: expandable section (collapsed sidebar
            // links straight to the first child instead).
            if (hasChildren && !isCollapsed) {
              const isOpen = !!openSections[item.path];
              const activeChildIdx = item.children!.findIndex((c) =>
                isChildActive(c.path, location.pathname),
              );
              return (
                <li key={item.path} className={styles.navListItem}>
                  <button
                    type="button"
                    className={`${styles.dropdownToggle} ${active ? styles.dropdownToggleActive : ''}`}
                    onClick={() => setOpenSections((prev) => ({ ...prev, [item.path]: !isOpen }))}
                    aria-expanded={isOpen}
                  >
                    <span className={`${styles.navLabel} ${active ? 'subtitle-1' : 'subtitle-2'}`}>
                      {item.langKey ? <Tr k={item.langKey} /> : item.label}
                    </span>
                    <svg
                      className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {isOpen && (
                    <div className={styles.childrenWrapper}>
                      <div className={styles.lineTrack}>
                        {activeChildIdx >= 0 && (
                          <span
                            className={styles.lineActive}
                            style={{ top: `calc(${activeChildIdx} * var(--child-item-height))` }}
                          />
                        )}
                      </div>
                      <ul className={styles.childList}>
                        {item.children!.map((child) => (
                          <li key={child.path} className={styles.childListItem}>
                            <NavLink
                              to={child.path}
                              className={`${styles.childItem} ${
                                isChildActive(child.path, location.pathname) ? styles.childItemActive : ''
                              }`}
                            >
                              {child.langKey ? <Tr k={child.langKey} /> : child.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            }

            const target = hasChildren ? item.children![0].path : item.path;
            return (
            <li key={item.path} className={styles.navListItem}>
              <NavLink
                to={target}
                className={`${styles.navItem} ${active ? styles.active : ''}`}
                onMouseEnter={() => setHoveredPath(item.path)}
                onMouseLeave={() => setHoveredPath(null)}
                title={isCollapsed ? (item.label) : undefined}
              >
                {() => {
                  const isHovered = hoveredPath === item.path;
                  return (
                    <>
                      <span className={`${styles.navDot} ${active ? styles.navDotActive : ''}`} aria-hidden="true" />
                      <span className={`${styles.navLabel} ${active || isHovered ? 'subtitle-1' : 'subtitle-2'}`}>
                        {item.langKey ? <Tr k={item.langKey} /> : item.label}
                      </span>
                    </>
                  );
                }}
              </NavLink>
              {isCollapsed && (
                <div className={styles.tooltip}>
                  {item.langKey ? item.label : item.label}
                </div>
              )}
            </li>
          )})}

        </ul>
      </nav>
    </aside>
  );
};
